import { useLayoutEffect, useRef, useState } from 'react';

export interface Line { label: string; vals: number[]; color: string; dash?: boolean }

export function useWidth<T extends HTMLElement>(): [React.RefObject<T>, number] {
  const ref = useRef<T>(null);
  const [w, setW] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setW(el.clientWidth));
    ro.observe(el);
    setW(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

const PAD = { l: 46, r: 92, t: 8, b: 18 };

export function LineChart({ xs, lines, height = 130, fmtY, fmtX, empty }: { xs: number[]; lines: Line[]; height?: number; fmtY: (v: number) => string; fmtX: (t: number) => string; empty?: string }) {
  const [ref, w] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const n = xs.length;
  const iw = Math.max(w - PAD.l - PAD.r, 10);
  const ih = height - PAD.t - PAD.b;
  const max = Math.max(1e-9, ...lines.flatMap((l) => l.vals.filter(Number.isFinite)));
  const top = niceTop(max);
  const x = (i: number) => PAD.l + (n <= 1 ? 0 : (i / (n - 1)) * iw);
  const y = (v: number) => PAD.t + ih - (Math.min(Math.max(v, 0), top) / top) * ih;
  const path = (vals: number[]) => vals.map((v, i) => (Number.isFinite(v) ? `${i === 0 || !Number.isFinite(vals[i - 1]) ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}` : '')).join('');
  const grid = [0, top / 2, top];
  const ends = lines.map((l) => ({ l, y: y(l.vals[n - 1] ?? 0) })).sort((a, b) => a.y - b.y);
  for (let i = 1; i < ends.length; i++) if (ends[i].y - ends[i - 1].y < 12) ends[i].y = ends[i - 1].y + 12;

  return (
    <div ref={ref} style={{ position: 'relative', width: '100%' }}>
      {w > 0 && n > 1 ? (
        <svg width={w} height={height} role="img" aria-label={lines.map((l) => l.label).join(', ')}
          onMouseMove={(e) => { const r = (e.currentTarget as SVGSVGElement).getBoundingClientRect(); const i = Math.round(((e.clientX - r.left - PAD.l) / iw) * (n - 1)); setHover(i >= 0 && i < n ? i : null); }}
          onMouseLeave={() => setHover(null)}>
          {grid.map((g) => (
            <g key={g}>
              <line x1={PAD.l} x2={PAD.l + iw} y1={y(g)} y2={y(g)} stroke="var(--hair)" />
              <text x={PAD.l - 6} y={y(g) + 3} textAnchor="end" fontSize="10" fill="var(--ink-45)">{fmtY(g)}</text>
            </g>
          ))}
          <text x={PAD.l} y={height - 4} fontSize="10" fill="var(--ink-45)">{fmtX(xs[0])}</text>
          <text x={PAD.l + iw} y={height - 4} fontSize="10" fill="var(--ink-45)" textAnchor="end">{fmtX(xs[n - 1])}</text>
          {lines.map((l) => <path key={l.label} d={path(l.vals)} fill="none" stroke={l.color} strokeWidth={2} strokeDasharray={l.dash ? '4 4' : undefined} strokeLinejoin="round" strokeLinecap="round" />)}
          {ends.map(({ l, y: ly }) => (
            <g key={l.label}>
              <line x1={PAD.l + iw + 4} x2={PAD.l + iw + 12} y1={ly} y2={ly} stroke={l.color} strokeWidth={2} strokeDasharray={l.dash ? '2 2' : undefined} />
              <text x={PAD.l + iw + 16} y={ly + 3} fontSize="10.5" fill="var(--ink-70)">{l.label}</text>
            </g>
          ))}
          {hover !== null && (
            <g pointerEvents="none">
              <line x1={x(hover)} x2={x(hover)} y1={PAD.t} y2={PAD.t + ih} stroke="var(--ink-40)" />
              {lines.map((l) => Number.isFinite(l.vals[hover]) && <circle key={l.label} cx={x(hover)} cy={y(l.vals[hover])} r={4} fill={l.color} stroke="var(--vs-surface)" strokeWidth={2} />)}
            </g>
          )}
        </svg>
      ) : (
        <div style={{ height, display: 'grid', placeItems: 'center', fontSize: 12, color: 'var(--ink-45)' }}>{empty ?? 'Collecting samples…'}</div>
      )}
      {hover !== null && n > 1 && (
        <div style={{ position: 'absolute', top: 4, left: Math.min(Math.max(x(hover) + 10, 0), Math.max(w - 170, 0)), pointerEvents: 'none', background: 'var(--vs-surface-raised)', border: '1px solid var(--vs-border)', borderRadius: 8, padding: '6px 8px', boxShadow: 'var(--vs-shadow-tooltip)', fontSize: 11.5, minWidth: 150 }}>
          <div style={{ color: 'var(--ink-55)', marginBottom: 3 }}>{fmtX(xs[hover])}</div>
          {lines.map((l) => (
            <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 10, height: 2, background: l.color, flex: 'none' }} />
              <span style={{ flex: 1, color: 'var(--ink-70)' }}>{l.label}</span>
              <span style={{ fontWeight: 600, color: 'var(--ink-96)' }}>{fmtY(l.vals[hover])}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function niceTop(v: number): number {
  const p = 10 ** Math.floor(Math.log10(v));
  for (const m of [1, 2, 2.5, 5, 10]) if (m * p >= v) return m * p;
  return 10 * p;
}
